import type { ReactNode } from 'react';
import { useRouter } from 'next/router';
import { replaceLang } from '@dx-ui/framework-i18n';
import { Popup } from '@dx-ui/osc-popup';
import { NavItemWithLink, NavItemWithPopup } from '../nav-items';
import {
  dispatchGlobalNavigationClickBasedOnLabel,
  sendInteractionReward,
  mapBrandCodeToSubmenuSurface,
} from '../../header.utilities';
import { DesktopNavigationPopupContents } from './navigation-popup-contents';
import type { MegaMenuProps } from '../../types';
import { useHeaderContext } from '../../context/header-context';

type DesktopMainNavProps = Required<Pick<MegaMenuProps, 'mainNavLinks'>> & {
  leadingChild?: ReactNode;
};

export const DesktopMainNav = ({ mainNavLinks, leadingChild }: DesktopMainNavProps) => {
  const { locale = 'en' } = useRouter();
  const {
    brand,
    visual: { isTranslucent, isReimagineThemeExperience, surfaceType },
    navigation: { onGlobalNavigationClick },
  } = useHeaderContext();

  return (
    <div className="flex min-h-[4.5rem] items-center">
      <div className="size-full overflow-x-visible">
        <div className="flex h-full items-center">
          {leadingChild}
          <ul className="flex h-full items-center rtl:space-x-reverse">
            {mainNavLinks.map(({ label, subMenu, link, onClick }) => {
              if (!subMenu?.length && link?.url) {
                return (
                  <NavItemWithLink
                    {...link}
                    url={replaceLang(link?.url, locale)}
                    section="navigation"
                    label={label}
                    isWide
                    key={label}
                    onClick={(event) => {
                      sendInteractionReward();
                      dispatchGlobalNavigationClickBasedOnLabel(
                        label,
                        undefined,
                        onGlobalNavigationClick
                      );
                      onClick?.();
                      link?.onClick?.(event);
                    }}
                  />
                );
              }
              if (subMenu?.length) {
                return (
                  <Popup key={label}>
                    <NavItemWithPopup
                      section="navigation"
                      label={label}
                      indicator="arrowhead-small-up"
                      rotateOnOpen
                      onClick={() => {
                        dispatchGlobalNavigationClickBasedOnLabel(
                          label,
                          undefined,
                          onGlobalNavigationClick
                        );
                      }}
                      experimentationConfiguration={link?.experimentationConfiguration}
                      surfaceType={
                        isTranslucent
                          ? surfaceType
                          : mapBrandCodeToSubmenuSurface(brand?.code, isReimagineThemeExperience)
                      }
                      isTranslucent={isTranslucent}
                    >
                      <DesktopNavigationPopupContents subMenu={subMenu} label={label} />
                    </NavItemWithPopup>
                  </Popup>
                );
              }
              return null;
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};
